import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { UserCheck } from 'lucide-react';
import { FollowButton } from './FollowButton';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Following {
  id: string;
  following_id: string;
  profiles: {
    id: string;
    display_name: string;
    avatar_url: string;
    username: string;
    is_verified: boolean;
  };
}

interface FollowingListProps {
  userId: string;
}

export const FollowingList = ({ userId }: FollowingListProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [following, setFollowing] = useState<Following[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFollowing();

    // Real-time subscription
    const channel = supabase
      .channel(`following:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'follows',
          filter: `follower_id=eq.${userId}`
        },
        () => {
          loadFollowing();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const loadFollowing = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('follows')
      .select(`
        id,
        following_id,
        profiles!follows_following_id_fkey(
          id,
          display_name,
          avatar_url,
          username,
          is_verified
        )
      `)
      .eq('follower_id', userId);

    if (!error && data) {
      setFollowing(data as any);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Card className="clean-card">
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="clean-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-primary" />
          Following ({following.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {following.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Not following anyone yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {following.map((follow) => (
              <div
                key={follow.id}
                className="flex items-center gap-3 p-3 border rounded-xl bg-white hover-lift cursor-pointer"
                onClick={() => navigate(`/user/${follow.profiles.id}`)}
              >
                <Avatar className="border-2 border-primary/10">
                  <AvatarImage src={follow.profiles.avatar_url} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {follow.profiles.display_name?.[0] || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground flex items-center gap-1">
                    {follow.profiles.display_name}
                    {follow.profiles.is_verified && (
                      <Badge variant="secondary" className="text-xs">✓</Badge>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">@{follow.profiles.username}</p>
                </div>
                {user && user.id !== follow.profiles.id && (
                  <FollowButton userId={follow.profiles.id} size="sm" />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
